import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  CurrencyDollarIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import ApiCustom from "../../utils/ApiCustom";
import CustomTableComponent from "../../components/CustomTableComponent";
import { formatDate } from "../../utils/helps";

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const userId = localStorage.getItem("userId");
const hasPermission = (slug) => {
  if (Number(userId) === 1) return true;
  return getUserPermissions().includes(slug);
};

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

const formatMoney = (n) => {
  const num = Number(n);
  return isNaN(num) ? "0 ₫" : num.toLocaleString("vi-VN") + " ₫";
};

const CustomSelector = ({ options, value, onChange }) => {
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShow(false);
    };
    if (show) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [show]);

  return (
    <div ref={ref} className="relative min-w-[160px] max-w-[280px]">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setShow((v) => !v);
        }}
        className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-md bg-white shadow-sm text-sm font-semibold hover:border-yellow-400 focus:outline-none transition"
      >
        <span className="truncate text-gray-700">
          {options.find((op) => op.id === value)?.title || "Chọn khoản hội phí"}
        </span>
        <ChevronDownIcon
          className={`w-5 h-5 ml-1 transition-transform ${
            show ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {show && (
          <motion.ul
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.13 } }}
            exit={{ opacity: 0, y: 10, transition: { duration: 0.11 } }}
            className="absolute left-0 z-20 mt-1 w-full rounded-md bg-white border border-gray-200 shadow-lg max-h-56 overflow-y-auto text-sm"
          >
            {options.length === 0 && (
              <li className="px-4 py-3 text-gray-400">Không có dữ liệu</li>
            )}
            {options.map((op) => (
              <li
                key={op.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setShow(false);
                  onChange(op.id);
                }}
                className={`px-4 py-3 cursor-pointer hover:bg-yellow-50 ${
                  op.id === value
                    ? "bg-yellow-100 font-semibold text-yellow-700"
                    : ""
                }`}
              >
                {op.title}
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

const tabs = [
  { key: "paid", label: "Hội viên đã đóng" },
  { key: "unpaid", label: "Hội viên chưa đóng" },
];

const MembershipFeeCard = () => {
  const [feeList, setFeeList] = useState([]);
  const [selectedFeeId, setSelectedFeeId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [modalFeeId, setModalFeeId] = useState(null);
  const [tab, setTab] = useState("paid");

  const [paidMembers, setPaidMembers] = useState([]);
  const [unpaidMembers, setUnpaidMembers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [paid, setPaid] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPaidMoney, setTotalPaidMoney] = useState(0);

  // NEW: search filter state
  const [searchValue, setSearchValue] = useState("");

  const canViewPaidAmount = hasPermission("MEMBERSHIP_FEE_VIEW_PAID_AMOUNT");

  /** Lấy danh sách các khoản hội phí */
  useEffect(() => {
    setLoading(true);
    ApiCustom.get("/membership-fee-shows")
      .then((res) => {
        const data = res?.data?.data?.data || [];
        const sorted = data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        setFeeList(sorted);
        if (sorted.length > 0) setSelectedFeeId(sorted[0].id);
      })
      .catch(() => setFeeList([]))
      .finally(() => setLoading(false));
  }, []);

  /** Lấy thống kê tổng */
  useEffect(() => {
    if (!selectedFeeId) return;
    setLoading(true);
    ApiCustom.get(`/membership-fee-shows/${selectedFeeId}`)
      .then((res) => {
        const detail = res?.data?.data?.data || {};
        const companies = (detail.companies || []).filter(
          (c) => (c?.approvalStatus ?? "").trim() === "Đã duyệt",
        );

        const paidList = companies.filter(
          (c) => c.CompaniesMustPayMembershipFee?.paid,
        );

        setPaid(paidList.length);
        setTotal(companies.length);
      })
      .catch(() => {
        setPaid(0);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [selectedFeeId]);

  /** Lấy chi tiết danh sách đã/ chưa đóng */
  useEffect(() => {
    if (!modalFeeId) return;
    setLoading(true);
    ApiCustom.get(`/membership-fee-shows/${modalFeeId}`)
      .then((res) => {
        const detail = res?.data?.data?.data || {};
        const companies = (detail.companies || []).filter(
          (c) => (c?.approvalStatus ?? "").trim() === "Đã duyệt",
        );

        const paidList = companies
          .filter((c) => c.CompaniesMustPayMembershipFee?.paid)
          .map((c) => ({
            id: c.id,
            name: c.companyName,
            rep:
              c?.users?.find(
                (u) => u?.CompaniesHasUsers?.isUserRepresentOfCompany,
              )?.userFullName || "",
            amount: Number(c.CompaniesMustPayMembershipFee?.amount) || 0,
            note: c.CompaniesMustPayMembershipFee?.note,
            updatedAt: c.CompaniesMustPayMembershipFee?.updatedAt || "",
          }));

        const unpaidList = companies
          .filter((c) => !c.CompaniesMustPayMembershipFee?.paid)
          .map((c) => ({
            id: c.id,
            name: c.companyName,
            rep:
              c?.users?.find(
                (u) => u?.CompaniesHasUsers?.isUserRepresentOfCompany,
              )?.userFullName || "",
            note: c.CompaniesMustPayMembershipFee?.note,
            updatedAt: c.CompaniesMustPayMembershipFee?.updatedAt || "",
          }));

        setPaidMembers(paidList);
        setUnpaidMembers(unpaidList);
        setTotalPaidMoney(
          paidList.reduce((sum, m) => sum + (Number(m.amount) || 0), 0),
        );
      })
      .catch(() => {
        setUnpaidMembers([]);
        setPaidMembers([]);
        setTotalPaidMoney(0);
      })
      .finally(() => setLoading(false));
  }, [modalFeeId, tab]);

  // headers
  const baseHeaders = [
    { key: "name", label: "Hội viên" },
    { key: "rep", label: "Đại diện" },
    {
      key: "updatedAt",
      label: "Cập nhật",
      render: (row) => formatDate(row?.updatedAt),
    },
    {
      key: "note",
      label: "Ghi chú",
      render: (row) => row?.note,
    },
  ];

  const paidHeaders = canViewPaidAmount
    ? [
        ...baseHeaders,
        {
          key: "amount",
          label: "Số tiền đã đóng",
          render: (row) => (
            <span className="text-green-700 font-bold text-lg">
              {formatMoney(row.amount)}
            </span>
          ),
        },
      ]
    : baseHeaders;

  // NEW: lọc client theo searchValue (áp dụng cho tab hiện tại)
  const filteredPaidMembers = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return paidMembers;
    return paidMembers.filter((m) => {
      const name = m.name?.toLowerCase() || "";
      const rep = m.rep?.toLowerCase() || "";
      return name.includes(q) || rep.includes(q);
    });
  }, [paidMembers, searchValue]);

  const filteredUnpaidMembers = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return unpaidMembers;
    return unpaidMembers.filter((m) => {
      const name = m.name?.toLowerCase() || "";
      const rep = m.rep?.toLowerCase() || "";
      return name.includes(q) || rep.includes(q);
    });
  }, [unpaidMembers, searchValue]);

  const tableData =
    tab === "paid" ? filteredPaidMembers : filteredUnpaidMembers;

  // NEW: số lượng theo tìm kiếm (theo danh sách đã filter)
  const paidCount = filteredPaidMembers.length;
  const unpaidCount = filteredUnpaidMembers.length;

  const handleOpenModal = () => {
    setModalFeeId(feeList?.[0]?.id || null);
    setTab("paid");
    setIsOpen(true);
  };

  return (
    <div className="w-full">
      {/* Card tổng */}
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="w-full h-full sm:w-auto shadow-md flex flex-col items-center text-center p-5 rounded-2xl border border-yellow-100 bg-white cursor-pointer"
        onClick={handleOpenModal}
      >
        <div className="p-3 bg-yellow-50 rounded-full mb-3">
          <CurrencyDollarIcon className="h-6 w-6 text-yellow-500" />
        </div>
        <span className="text-lg text-black mb-1">
          Tổng hội viên đã đóng hội phí
        </span>
        <span className="text-3xl font-bold">
          {loading ? (
            "..."
          ) : (
            <>
              {paid} / {total}
            </>
          )}
        </span>
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            {...animation}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              {...animation}
              className="relative bg-white w-full max-w-6xl mx-2 rounded-2xl p-6 sm:p-10 shadow-xl border border-yellow-100"
              style={{ maxHeight: "90vh", overflow: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* nút close cố định góc phải trên */}
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 rounded-md"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              {/* Header responsive */}
              <div className="flex flex-col gap-3 mb-4 md:mb-2 md:flex-row md:items-start md:justify-between">
                {/* Tiêu đề */}
                <h2 className="text-xl font-bold text-gray-900 md:text-2xl leading-snug pr-10">
                  {tab === "paid"
                    ? "Danh sách hội viên đã đóng hội phí"
                    : "Danh sách hội viên chưa đóng hội phí"}
                </h2>

                {/* Tabs */}
                <div className="flex w-full justify-between gap-2 md:w-auto md:justify-start md:flex-none">
                  {tabs.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setTab(t.key)}
                      className={`flex-1 md:flex-none px-3 py-2 rounded-md text-[13px] sm:text-sm font-semibold border transition text-center
                        ${
                          tab === t.key
                            ? "bg-yellow-50 border-yellow-400 text-yellow-700 shadow"
                            : "bg-white border-gray-200 text-gray-500 hover:border-yellow-300"
                        }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Selector + Tổng tiền + Search */}
              <div className="flex flex-col gap-3 mb-5 md:flex-row md:items-start md:justify-between">
                {/* Cột trái (khoản hội phí) */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <span className="font-medium text-gray-500 text-sm sm:text-base">
                    Khoản hội phí:
                  </span>
                  <CustomSelector
                    options={feeList}
                    value={modalFeeId}
                    onChange={setModalFeeId}
                  />
                </div>

                {/* Cột phải (Số lượng + Tổng đã đóng + Search) */}
                <div className="flex flex-col w-full gap-3 md:w-auto md:flex-row md:items-center md:gap-4">
                  {/* Số lượng (style giống số tiền: font + color) */}
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-500 text-sm sm:text-base whitespace-nowrap">
                      Số lượng:
                    </span>
                    <span className="font-semibold text-green-700 text-xl sm:text-2xl whitespace-nowrap">
                      {tab === "paid" ? paidCount : unpaidCount}
                    </span>
                  </div>

                  {tab === "paid" && canViewPaidAmount && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-500 text-sm sm:text-base whitespace-nowrap">
                        Tổng đã đóng:
                      </span>
                      <span className="font-semibold text-green-700 text-xl sm:text-2xl whitespace-nowrap">
                        {formatMoney(totalPaidMoney)}
                      </span>
                    </div>
                  )}

                  {/* Ô tìm kiếm */}
                  <input
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Tìm kiếm hội viên..."
                    className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                  />
                </div>
              </div>

              {/* Bảng */}
              <div
                style={{ maxHeight: 440, overflowY: "auto" }}
                className="mt-2"
              >
                <CustomTableComponent
                  headers={tab === "paid" ? paidHeaders : baseHeaders}
                  data={tableData}
                />
                {tab === "unpaid" && tableData.length === 0 && (
                  <div className="text-center text-base text-gray-500 my-7">
                    Tất cả hội viên đã đóng hội phí này
                  </div>
                )}
                {tab === "paid" && tableData.length === 0 && (
                  <div className="text-center text-base text-gray-500 my-7">
                    Không có hội viên đã đóng phí này
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MembershipFeeCard;
